import {
  Alert,
  Box,
  Button,
  Checkbox,
  FileUpload,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { groupBy, isError } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { getErrorMessage, getJamClient } from '@/src/api/sdk/JamClient';
import { useAuth } from '@/src/store/auth.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { parseCsv } from '@/src/utils/csv.utils';
import { isValidEmail } from '@/src/utils/validation.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { EventPrivacyType } from '@/src/types/EventPrivacyType';
import { Team } from '@/src/types/Team';

export interface ImportTeamsModalProps {
  eventName: string;
  eventPrivacyType: EventPrivacyType | null;
  existingTeams?: Team[];
}

type RowStatus = 'pending' | 'loading' | 'success' | 'error' | 'cancelled';

interface UserRow {
  id: string;
  teamName: string;
  email: string;
  legacyLogin: string;
  vibeId: string;
  status?: RowStatus;
  error?: string;
}

export const ImportTeamsModal = NiceModal.create((props: ImportTeamsModalProps) => {
  const { eventName, eventPrivacyType, existingTeams = [] } = props;
  const modal = useModal();
  const { t } = useTranslation();
  const { authClient } = useAuth();
  const jamClient = getJamClient(authClient);
  const { addSuccessFlashbar } = useFlashbars();

  const [csvFile, setCsvFile] = useState<File[]>([]);
  const [csvError, setCsvError] = useState('');
  const [rows, setRows] = useState<UserRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [mergeExisting, setMergeExisting] = useState(false);
  const cancelledRef = useRef(false);

  const updateRow = (id: string, patch: Partial<UserRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const updateTeamRows = (teamName: string, patch: Partial<UserRow>) => {
    setRows((prev) => prev.map((r) => (r.teamName === teamName ? { ...r, ...patch } : r)));
  };

  const clearState = () => {
    setCsvFile([]);
    setCsvError('');
    setRows([]);
    cancelledRef.current = false;
  };

  const handleCancel = () => {
    cancelledRef.current = true;
  };

  const handleDismiss = () => {
    if (importing) {
      return;
    }

    clearState();
    modal.hide();
  };

  const handleCsvUpload = async (files: File[]) => {
    setCsvError('');
    setRows([]);
    setCsvFile(files);

    if (!files.length || !files[0]) return;

    const validator = (row: UserRow) => {
      if (!row.teamName || !row.email || !row.legacyLogin) {
        return t(i18nKeys.participants.labels.importTeamsMissingFields);
      }

      if (!isValidEmail(row.email)) {
        return t(i18nKeys.participants.labels.importTeamsInvalidEmail, { email: row.email });
      }
    };

    const { data, errors } = await parseCsv<UserRow>(files[0], validator);

    if (errors.length > 0) {
      setCsvError(t(i18nKeys.participants.labels.importTeamsCsvError));
      setCsvFile([]);
      return;
    }

    if (data.length === 0) {
      setCsvError(t(i18nKeys.participants.labels.importTeamsNoData));
      setCsvFile([]);
      return;
    }

    setRows(data.map((row) => ({ ...row, id: uuidv4() })).sort((a, b) => a.teamName.localeCompare(b.teamName)));
  };

  /** Create the team. Returns true if the team was created (or already exists). */
  const createTeam = async (teamName: string): Promise<boolean> => {
    const teamAlreadyExists = existingTeams.some((team) => team.name === teamName);

    if (teamAlreadyExists) {
      if (mergeExisting) {
        return true;
      }

      updateTeamRows(teamName, {
        status: 'error',
        error: t(i18nKeys.participants.labels.importTeamsTeamAlreadyExists, { teamName }),
      });
      return false;
    }

    const createResult = await jamClient.createEventTeam({
      eventName,
      teamName,
      password: uuidv4(),
    });

    if (isError(createResult)) {
      updateTeamRows(teamName, { status: 'error', error: getErrorMessage(createResult) });
      return false;
    }

    return true;
  };

  /** Load all invited participant emails for the event. */
  const loadInvitedParticipants = async (): Promise<Set<string>> => {
    const invitedEmails: string[] = [];
    let lastToken: string | undefined;

    do {
      const res = await jamClient.searchEventParticipants({
        eventName,
        pageSize: 100,
        ...(lastToken && { lastToken }),
      });

      if (isError(res)) {
        break;
      }

      if (res.participantsEmail) {
        invitedEmails.push(...res.participantsEmail);
      }

      lastToken = res.nextToken;
    } while (lastToken);

    return new Set(invitedEmails.map((e) => e.toLowerCase()));
  };

  /** Invite team members via email for private-invite events. Returns true on success. */
  const inviteTeamParticipants = async (
    teamName: string,
    members: UserRow[],
    alreadyInvited: Set<string>
  ): Promise<boolean> => {
    if (eventPrivacyType !== EventPrivacyType.PRIVATE_INVITE) {
      return true;
    }

    const teamEmails = members.map((m) => m.email).filter((email) => !alreadyInvited.has(email.toLowerCase()));

    if (teamEmails.length === 0) {
      return true;
    }

    const inviteResult = await jamClient.inviteEventParticipants({
      eventName,
      request: { emails: teamEmails },
    });

    if (isError(inviteResult)) {
      updateTeamRows(teamName, { status: 'error', error: getErrorMessage(inviteResult) });
      return false;
    }

    return true;
  };

  /** Assign each member to the team. Returns true if all assignments succeeded. */
  const assignTeamMembers = async (teamName: string, members: UserRow[]): Promise<boolean> => {
    const existingTeam = existingTeams.find((team) => team.name === teamName);
    const existingMemberIds = new Set(existingTeam?.members.map((m) => m.userId) ?? []);

    let allSucceeded = true;

    for (const member of members) {
      if (cancelledRef.current) {
        updateRow(member.id, { status: 'cancelled', error: undefined });
        continue;
      }

      if (existingMemberIds.has(member.legacyLogin)) {
        updateRow(member.id, { status: 'success' });
        continue;
      }

      const assignResult = await jamClient.assignEventUserTeam({
        eventName,
        userId: member.legacyLogin,
        teamName,
      });

      if (isError(assignResult)) {
        updateRow(member.id, { status: 'error', error: getErrorMessage(assignResult) });

        allSucceeded = false;
      } else {
        updateRow(member.id, { status: 'success' });
      }
    }

    return allSucceeded;
  };

  const handleImport = async () => {
    setImporting(true);
    cancelledRef.current = false;

    const teamMap = groupBy(rows, (r) => r.teamName);
    const teamNames = Object.keys(teamMap);

    for (const teamName of teamNames) {
      updateTeamRows(teamName, { status: 'pending' });
    }

    // Load already-invited participants once before processing teams
    const alreadyInvited =
      eventPrivacyType === EventPrivacyType.PRIVATE_INVITE ? await loadInvitedParticipants() : new Set<string>();

    let hasError = false;

    for (const teamName of teamNames) {
      if (cancelledRef.current) {
        updateTeamRows(teamName, { status: 'cancelled', error: undefined });
        continue;
      }

      updateTeamRows(teamName, { status: 'loading' });

      const created = await createTeam(teamName);
      if (!created) {
        hasError = true;
        continue;
      }

      if (cancelledRef.current) {
        updateTeamRows(teamName, { status: 'cancelled', error: undefined });
        continue;
      }

      const invited = await inviteTeamParticipants(teamName, teamMap[teamName], alreadyInvited);
      if (!invited) {
        hasError = true;
        continue;
      }

      if (cancelledRef.current) {
        updateTeamRows(teamName, { status: 'cancelled', error: undefined });
        continue;
      }

      const assigned = await assignTeamMembers(teamName, teamMap[teamName]);
      if (!assigned) {
        hasError = true;
      }
    }

    if (!hasError && !cancelledRef.current) {
      addSuccessFlashbar(t(i18nKeys.participants.labels.importTeamsSuccess, { count: teamNames.length }));
      clearState();
      modal.hide();
    }

    setImporting(false);
  };

  const uniqueTeamCount = new Set(rows.map((r) => r.teamName)).size;

  return (
    <Modal
      visible={modal.visible}
      onDismiss={handleDismiss}
      header={t(i18nKeys.participants.headers.importTeams)}
      size="x-large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {rows.length > 0 && !importing && (
              <Button variant="normal" onClick={clearState}>
                {t(i18nKeys.general.clear)}
              </Button>
            )}
            {importing ? (
              <Button variant="normal" onClick={handleCancel} data-testid="import-teams-cancel-btn">
                {t(i18nKeys.participants.buttons.importTeamsCancelImport)}
              </Button>
            ) : (
              <Button variant="link" onClick={handleDismiss}>
                {t(i18nKeys.general.cancel)}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => void handleImport()}
              disabled={rows.length === 0 || importing}
              loading={importing}
            >
              {t(i18nKeys.participants.buttons.importTeamsConfirm, { count: uniqueTeamCount })}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>{t(i18nKeys.participants.labels.importTeamsDescription)}</Box>

        <Checkbox
          checked={mergeExisting}
          disabled={importing}
          onChange={({ detail }) => setMergeExisting(detail.checked)}
          data-testid="import-teams-merge-checkbox"
        >
          {t(i18nKeys.participants.labels.importTeamsMergeExisting)}
        </Checkbox>

        <FileUpload
          onChange={({ detail }) => void handleCsvUpload(detail.value)}
          accept="text/csv"
          value={csvFile}
          i18nStrings={{
            uploadButtonText: () => t(i18nKeys.participants.addParticipants.importCSV.chooseFile),
            dropzoneText: () => t(i18nKeys.participants.addParticipants.importCSV.dropFileToUpload),
            removeFileAriaLabel: () => t(i18nKeys.participants.addParticipants.importCSV.removeFile),
            limitShowFewer: t(i18nKeys.participants.addParticipants.importCSV.showFewerFiles),
            limitShowMore: t(i18nKeys.participants.addParticipants.importCSV.showMoreFiles),
            errorIconAriaLabel: t(i18nKeys.participants.addParticipants.importCSV.error),
          }}
          data-testid="import-teams-file-upload"
        />

        {csvError && <Alert type="error">{csvError}</Alert>}

        {rows.length > 0 && (
          <div style={{ maxHeight: '600px', overflow: 'auto' }}>
            <Box padding={{ right: 'm' }}>
              <Table
                items={rows}
                trackBy="id"
                columnDefinitions={[
                  {
                    id: 'teamName',
                    header: t(i18nKeys.participants.headers.teamName),
                    cell: (item) => item.teamName,
                  },
                  {
                    id: 'email',
                    header: t(i18nKeys.participants.headers.email),
                    cell: (item) => item.email,
                  },
                  {
                    id: 'status',
                    header: t(i18nKeys.general.status),
                    cell: (item) => {
                      if (!item.status) return null;
                      switch (item.status) {
                        case 'pending':
                          return <StatusIndicator type="pending">{t(i18nKeys.general.pending)}</StatusIndicator>;
                        case 'loading':
                          return (
                            <StatusIndicator type="loading" colorOverride="blue">
                              {t(i18nKeys.general.loading)}
                            </StatusIndicator>
                          );
                        case 'success':
                          return <StatusIndicator type="success">{t(i18nKeys.general.success)}</StatusIndicator>;
                        case 'error':
                          return <StatusIndicator type="error">{item.error}</StatusIndicator>;
                        case 'cancelled':
                          return <StatusIndicator type="stopped">{t(i18nKeys.general.cancelled)}</StatusIndicator>;
                        default:
                          return null;
                      }
                    },
                  },
                ]}
                variant="borderless"
                stickyHeader
                data-testid="import-teams-table"
                header={
                  <StatusIndicator type="info">
                    {t(i18nKeys.participants.buttons.importTeamsConfirm, { count: uniqueTeamCount })}
                    {` — ${t(i18nKeys.participants.labels.importTeamsParticipantCount, { count: rows.length })}`}
                  </StatusIndicator>
                }
              />
            </Box>
          </div>
        )}
      </SpaceBetween>
    </Modal>
  );
});
